import React, { createContext, useContext, useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is logged in on app start
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (token && userData) {
      try {
        setUser(JSON.parse(userData));
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      } catch (error) {
        console.error('Error parsing user data:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
    setLoading(false);
  }, []);

  const requestOtp = async (email, role) => {
    try {
      setLoading(true);
      const endpoint =
        role === 'admin' ? '/api/auth/admin/request-otp' : '/api/auth/staff/request-otp';
      const response = await api.post(endpoint, { email });
      const msg =
        response.data?.message || 'Verification code sent. Check your email.';
      toast.success(msg);
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.response?.data?.errors?.[0]?.msg ||
        'Could not send verification code.';
      toast.error(message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (email, code, role) => {
    try {
      setLoading(true);
      const endpoint =
        role === 'admin' ? '/api/auth/admin/verify-otp' : '/api/auth/staff/verify-otp';
      const response = await api.post(endpoint, { email, code });
      const { token, admin, staff } = response.data;
      const userData = admin || staff;
      userData.role = role;

      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(userData));
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser(userData);

      toast.success(`Welcome back, ${userData.name}!`);
      return userData;
    } catch (error) {
      const message = error.response?.data?.message || 'Verification failed';
      toast.error(message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      // Call logout API to log the event
      if (user?.role === 'admin') {
        await api.post('/api/auth/admin/logout');
      } else if (user?.role === 'staff') {
        await api.post('/api/auth/staff/logout');
      }
    } catch (error) {
      console.error('Logout API error:', error);
      // Continue with logout even if API call fails
    }
    
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
    toast.info('Logged out successfully');
  };

  const updateUser = (updatedUserData) => {
    setUser(updatedUserData);
    localStorage.setItem('user', JSON.stringify(updatedUserData));
  };

  const value = {
    user,
    requestOtp,
    verifyOtp,
    logout,
    updateUser,
    loading,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    isStaff: user?.role === 'staff'
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 